import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { usePageContents } from '../contexts/PageContentContext';

interface CourseAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAgree: () => void;
  programName: string;
  className?: string;
}

const CourseAgreementModal: React.FC<CourseAgreementModalProps> = ({
  isOpen,
  onClose,
  onAgree,
  programName,
  className: classNameProp,
}) => {
  const [agreedSafety, setAgreedSafety] = useState(false);
  const [agreedOperation, setAgreedOperation] = useState(false);
  const { getPageContent, loading } = usePageContents();

  // 모달 열릴 때 동의 체크 초기화
  useEffect(() => {
    if (isOpen) {
      setAgreedSafety(false);
      setAgreedOperation(false);
    }
  }, [isOpen]);

  // 동의서 내용 가져오기
  const safetyAgreementContent = getPageContent('course_agreement')?.description || '';
  const operationAgreementContent = getPageContent('course_operation_agreement')?.description || '';

  // 둘 다 동의해야 신청 가능
  const allAgreed = agreedSafety && agreedOperation;

  const handleAgree = () => {
    if (allAgreed) {
      onAgree();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">수강신청 동의서</h2>
            <p className="text-sm text-gray-600 mt-1">{programName}{classNameProp ? ` - ${classNameProp}` : ''}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#48c9a6]"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 1. 안전관리 동의서 */}
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3">1. 안전관리 동의서</h3>
                <div className="border border-gray-300 rounded-lg p-4 h-48 overflow-y-auto bg-gray-50 text-sm text-gray-700 leading-relaxed mb-3">
                  {safetyAgreementContent ? (
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: safetyAgreementContent }}
                    />
                  ) : (
                    <div className="text-gray-500">동의서 내용이 없습니다.</div>
                  )}
                </div>
                {/* 동의 체크박스 */}
                <div className={`p-3 rounded-lg border-2 ${agreedSafety ? 'bg-[#48c9a6]/10 border-[#48c9a6]' : 'bg-gray-50 border-gray-200'}`}>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedSafety}
                      onChange={(e) => setAgreedSafety(e.target.checked)}
                      className="w-5 h-5 mt-0.5 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      위 안전관리 동의서 내용을 모두 읽었으며, 이에 동의합니다.
                    </span>
                  </label>
                </div>
              </div>

              {/* 2. 수강 및 교육과정 운영 동의서 */}
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3">2. 수강 및 교육과정 운영 동의서</h3>
                <div className="border border-gray-300 rounded-lg p-4 h-48 overflow-y-auto bg-gray-50 text-sm text-gray-700 leading-relaxed mb-3">
                  {operationAgreementContent ? (
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: operationAgreementContent }}
                    />
                  ) : (
                    <div className="text-gray-500">동의서 내용이 없습니다.</div>
                  )}
                </div>
                {/* 동의 체크박스 */}
                <div className={`p-3 rounded-lg border-2 ${agreedOperation ? 'bg-[#48c9a6]/10 border-[#48c9a6]' : 'bg-gray-50 border-gray-200'}`}>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedOperation}
                      onChange={(e) => setAgreedOperation(e.target.checked)}
                      className="w-5 h-5 mt-0.5 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      위 수강 및 교육과정 운영 동의서 내용을 모두 읽었으며, 이에 동의합니다.
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleAgree}
            disabled={!allAgreed}
            className="flex-1 py-3 px-4 border border-transparent text-base font-medium rounded-lg text-white bg-[#48c9a6] hover:bg-[#3ab89a] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {allAgreed ? '동의 후 신청하기' : `동의 필요 (${[agreedSafety, agreedOperation].filter(Boolean).length}/2)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseAgreementModal;
